import { AnyAction, createSlice, Slice } from '@reduxjs/toolkit';
import { TAppDispatch, TState } from '~/src/store';

import { IGroupedProps, TPropValues } from '~/src/services/properties';
import { updateElement, updateProject } from '~/src/services/properties/updateStore';
import { addToStore } from '~/src/services/blocks/updateStore';
import blockConfigs from '~/src/services/blocks/configs';

import { prepareProperties } from '~/src/services/properties/prepareProperties';
import { EElementType } from '~/src/services/projects';

interface IPropertiesSlice {
    propsLabels?: IGroupedProps[];
    propsValues?: TPropValues,
}

const propertiesSlice: Slice<IPropertiesSlice> = createSlice({
    name: 'properties',
    initialState: {
        propsLabels: undefined,
        propsValues: undefined,
    },
    reducers: {
        change: (state: IPropertiesSlice, action: AnyAction) => {
            delete action.payload.project;
            state.propsValues = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase('currentProject/select', (state, action: AnyAction) => {
            const { items, props } = prepareProperties(action.payload, action.payload.type, blockConfigs);
            return {
                ...state,
                propsLabels: items,
                propsValues: props,
            };
        });
        builder.addCase('currentProject/clear', (state) => {
            state.propsLabels = undefined;
            state.propsValues = undefined;
        });
    },
});

export const { change } = propertiesSlice.actions;

export const changeProperty = (name: string, value: string) => {
    return (dispatch: TAppDispatch, getState: () => TState): void => {
        const { currentProject: { selectedItem: { id, type }, project }, properties: { propsValues } } = getState();

        const action = {
            ...propsValues,
            [name]: value,
        };

        if (name === 'addElement') {
            action.project = addToStore(project, value, id);
        } else {
            action.project = type === EElementType.Project
                    ? updateProject(project, name, value)
                    : updateElement(project, id, name, value);
        }
        dispatch(change(action));
    };
};

export const selectProperties = (state: TState): IPropertiesSlice =>
    state.properties;
export default propertiesSlice.reducer;
